<template>
  <ui-button
    class="ui-period-picker"
    icon="fas fa-calendar-alt"
    :text="pText"
    :menu-content-class="pMenuContentClass"
    :menu-props="{ locationStrategy: placePeriodPickerMenu }"
    @update:menu="$emit('menu', $event)"
    ref="picker"
  >
    <template #menu>
      <div
        class="ui-period-picker__menu"
        :class="{
          'ui-period-picker__menu-small': pickerColumns === 1,
        }"
      >
        <div class="ui-period-picker__menu-content">
          <div class="ui-period-picker__menu-picker-wrapper">
            <ui-date-picker
              v-model="pRange"
              :columns="pickerColumns"
              @drag="handleDrag"
              ref="datePicker"
            ></ui-date-picker>
          </div>

          <slot name="append"></slot>
        </div>

        <div class="ui-period-picker__menu-footer flex-column">
          <div class="buttons-first-row">
            <div class="ui-period-picker__menu-range">
              <ui-chip class="mr-4 ml-0 hidden-sm-and-down">{{ dateFrom }}</ui-chip>
              <ui-select
                v-model="timeFrom"
                class="picker-select mr-4 ml-0"
                :items="start"
                :hide-details="1"
                :autocomplete="false"
              ></ui-select>

              <div class="mr-4">–</div>

              <ui-chip class="mr-4 hidden-sm-and-down">{{ dateTo }}</ui-chip>
              <ui-select
                v-model="timeTo"
                class="picker-select"
                :items="end"
                :hide-details="1"
                :autocomplete="false"
              ></ui-select>
            </div>

            <div class="ui-period-picker__menu-range-actions flex--grow">
              <slot name="actions">
                <ui-button
                  icon="fas fa-times"
                  :text="t('Cancel')"
                  @click="$emit('reset', $event)"
                ></ui-button>

                <ui-button
                  button-style="accent"
                  icon="fas fa-check"
                  :text="t('Apply')"
                  @click="$emit('apply', $event)"
                ></ui-button>
              </slot>
            </div>
          </div>
          <ui-select
            v-model="range.timezone"
            class="timezone"
            :items="timezoneList"
            :hide-details="1"
            name="timezone"
            v-if="showTimezone"
            item-text="name"
            item-value="zone"
            :autocomplete="false"
          ></ui-select>
        </div>
      </div>
    </template>
  </ui-button>
</template>

<script setup lang="ts">
import { useUiPeriodPicker } from './useUiPeriodPicker';

const viewportMargin = 12;
const nudgeRight = 10;
const nudgeBottom = 12;

const placePeriodPickerMenu = (
  data: { target: { value: Element | [number, number] | null }; contentEl: { value: HTMLElement | null } },
  _props: unknown,
  contentStyles: { value: Record<string, string> },
) => {
  const updateLocation = () => {
    const target = data.target.value;
    const content = data.contentEl.value;
    if (!target || !content || Array.isArray(target)) return;

    const targetBox = target.getBoundingClientRect();
    const menuWidth = content.offsetWidth;
    const menuHeight = content.offsetHeight;
    if (!menuWidth || !menuHeight) return;

    let menuLeft = targetBox.right + nudgeRight - menuWidth;
    const maxLeft = window.innerWidth - viewportMargin - menuWidth;
    if (menuLeft < viewportMargin) menuLeft = viewportMargin;
    if (menuLeft > maxLeft) menuLeft = Math.max(viewportMargin, maxLeft);

    let menuTop = targetBox.bottom + nudgeBottom;
    const maxTop = window.innerHeight - viewportMargin - menuHeight;
    if (menuTop > maxTop) menuTop = Math.max(viewportMargin, maxTop);

    contentStyles.value = {
      position: 'fixed',
      left: `${menuLeft}px`,
      top: `${menuTop}px`,
    };
  };

  requestAnimationFrame(() => {
    updateLocation();
    requestAnimationFrame(() => updateLocation());
  });

  return { updateLocation };
};

const props = withDefaults(
  defineProps<{
    text?: string;
    dateValue?: string;
    columns?: number;
    value: TimeRange;
    format?: string;
    timeInTitle?: string | boolean;
    menuContentClass?: string;
    showTimezone?: boolean;
  }>(),
  {
    format: 'YYYY-MM-DD HH:mm:ss',
    showTimezone: true,
  },
);

const emit = defineEmits<{
  input: [value: TimeRange];
  apply: [event: Event];
  reset: [event: Event];
  menu: [open: boolean];
}>();

const {
  t,
  timezoneList,
  picker,
  datePicker,
  range,
  pickerColumns,
  pMenuContentClass,
  pText,
  pRange,
  dateFrom,
  dateTo,
  timeFrom,
  timeTo,
  start,
  end,
  handleDrag,
  toggleMenu,
  redrawMenu,
  adjustPageRange,
} = useUiPeriodPicker(props, (value) => emit('input', value));

defineExpose({
  toggleMenu,
  redrawMenu,
  adjustPageRange,
});
</script>

<style lang="scss" scoped>
.picker-select {
  width: 66px;
  margin-left: 4px;
  margin-right: 4px;
}
.buttons-first-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.timezone {
  margin: 4px 8px 0 0;
  display: flex;
  flex-grow: 0;
  max-width: 340px;
  :deep(.ui-select__wrapper) {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@/styles/ui/period-picker.scss';
</style>
